import Loader from 'components/ui/LoadingIndicator';
import Link from 'components/ui/AppLink';
import { MessageView } from 'components/ui/Feedback';
import ContainerBox from 'components/ui/ContainerBox';
import {
    Box,
    Breadcrumbs,
    Typography,
    Typography as BreadcrumbsText,
} from '@mui/material';
import React from 'react';
import { useParams, Router, useHistory } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import moment from 'moment';
import { Article, User, UserArticleStats } from '../../../shared/types';
import AdminList from '../../ui/admin/AdminList';
import { ColDef } from 'ag-grid-community';
import gql from 'graphql-tag';
import {
    configureLongDateFormat,
    formatDisplayDate,
} from '../../../utils/date.utils';
import { baseUrls } from '../../../utils/baseUrls.utils';
import { sanitizeRouteId } from '../../../utils/route-params.utils';
import { isValidMongoId } from '../../../utils/id-validation.utils';

configureLongDateFormat();

interface ArticleStatRowData {
    _id: string;
    name: string;
    email: string;
    views: number;
    lastViewed: string;
}

interface ArticleStatsBreadcrumbsProps {
    article: Article;
}

interface ArticleStatsBarProps {
    article: Article;
}

interface UserDetailsCellProps {
    data: User;
    context: {
        history: ReturnType<typeof useHistory>;
    };
}

export const FIND_ARTICLE_STATS_BY_ID = gql`
    query findArticleStatsById($id: ID!) {
        document: findArticleStatsById(id: $id) {
            _id
            author
            title
            html
            publishedBy {
                _id
                name
                type
            }
            contributors {
                _id
                name
            }
            categories {
                _id
                name
            }
            tags {
                _id
                name
            }
            state
            created
            summary
            version
            lastEdited
            publishDate
            views
            viewers {
                _id
                name
                email
                articleStats {
                    articleId
                    views
                    lastViewed
                }
            }
        }
    }
`;

const articleStatsBarColumns = [
    {
        id: 'article-stats-status-bar-views',
        title: 'Article views',
        key: 'articleViews',
        renderCallback: ({ views = 0 }: Article) =>
            `${views} view${views > 1 ? 's' : ''}`,
    },
    {
        id: 'article-stats-status-bar-unique-viewers',
        title: 'Unique viewers',
        renderCallback: ({ viewers = [] }: Article) =>
            `${viewers?.length} user${viewers?.length > 1 ? 's' : ''}`,
    },
    {
        id: 'article-stats-status-bar-published',
        title: 'Published',
        renderCallback: (item: Article) =>
            !item.publishDate
                ? undefined
                : moment(item.publishDate).format('DD MMM YYYY HH:mm'),
    },
    {
        id: 'article-stats-status-bar-last-updated',
        title: 'Last updated',
        renderCallback: (item: Article) =>
            !item.lastEdited
                ? undefined
                : moment(item.lastEdited).format('DD MMM YYYY HH:mm'),
    },
];

const UserDetailsCell = ({ data: user, context }: UserDetailsCellProps) => {
    if (!context?.history)
        throw new Error(
            'React router history must be added to ag-grid context'
        );

    if (!user) return null;

    // TODO need get optedOutFromMarketing flag from the config, required discussion
    //	if (optedOutFromMarketing) return <>undisclosed</>;

    return (
        <Router history={context.history}>
            {user.name ? (
                <>
                    <Link
                        sx={{ mr: 0.6 }}
                        to={baseUrls.publisher.users.edit.replace(
                            ':_id',
                            window.encodeURIComponent(user._id)
                        )}
                    >
                        {user.name}
                    </Link>
                    <Typography
                        component="span"
                        variant="body2"
                    >{`<${user.email}>`}</Typography>
                </>
            ) : (
                <Typography component="span" variant="body2">
                    {user.email || 'Anonymous'}
                </Typography>
            )}
        </Router>
    );
};

const ArticleStatsBreadcrumbs = ({ article }: ArticleStatsBreadcrumbsProps) => (
    <Breadcrumbs>
        <Link to={baseUrls.publisher.articles.list}>Articles</Link>
        <Link to={`${baseUrls.publisher.articles.list}/edit/${article._id}`}>
            {article.title}
        </Link>
        <BreadcrumbsText>View stats</BreadcrumbsText>
    </Breadcrumbs>
);

const ArticleStatsBar = ({ article }: ArticleStatsBarProps) => (
    <Box
        sx={{
            display: 'flex',
            p: 2,
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
        }}
    >
        {articleStatsBarColumns.map((item, index) => (
            <Box id={item.id} sx={{ mr: 4 }} key={index}>
                <Typography
                    variant="caption"
                    sx={{
                        color: 'text.secondary',
                        mb: 0.25,
                    }}
                >
                    {item.title}
                </Typography>
                <Typography variant="body1">
                    {item.renderCallback(article)}
                </Typography>
            </Box>
        ))}
    </Box>
);

const columnDefs: ColDef[] = [
    {
        headerName: 'User details',
        field: '_id',
        width: 800,
        cellRenderer: UserDetailsCell,
        menuTabs: [],
    },
    {
        headerName: 'Views',
        field: 'views',
        width: 160,
        menuTabs: [],
    },
    {
        headerName: 'Last visit',
        field: 'lastViewed',
        width: 224,
        cellRenderer: (props: { value: string }) =>
            formatDisplayDate(props.value, false),
        menuTabs: [],
    },
];

const ArticleStatsPage = (): JSX.Element => {
    const { push } = useHistory();
    const { _id: routeId } = useParams<{ _id?: string }>();
    const _id = sanitizeRouteId(routeId);
    const isValidArticleId = isValidMongoId(_id);

    const errorMessage = (
        <MessageView
            styleName="primary"
            size="large"
            title="Article not found"
            primaryAction={{
                label: 'Return to Neon Insights',
                onClick: () => push('/'),
            }}
        />
    );

    const { data, loading } = useQuery(FIND_ARTICLE_STATS_BY_ID, {
        fetchPolicy: 'network-only',
        skip: !isValidArticleId,
        variables: {
            id: _id,
        },
    });

    if (!isValidArticleId) {
        return errorMessage;
    }

    if (loading) {
        return <Loader />;
    }

    const article = data?.document;
    if (!article) {
        return errorMessage;
    }

    const articleStatRowData: ArticleStatRowData[] = (article.viewers || [])
        .sort(
            (
                { articleStats: a = {} as UserArticleStats }: User,
                { articleStats: b = {} as UserArticleStats }: User
            ) =>
                new Date(b.lastViewed).getTime() -
                new Date(a.lastViewed).getTime()
        )
        .map(
            ({
                _id,
                name,
                email,
                articleStats = {} as UserArticleStats,
            }: Partial<User>) => {
                return {
                    _id,
                    name,
                    email,
                    views: articleStats.views,
                    lastViewed: articleStats.lastViewed,
                } as ArticleStatRowData;
            }
        );

    const statusBarArticleViews: number = articleStatRowData
        .map((c) => c.views)
        .reduce((a, b) => a + b, 0);

    return (
        <ContainerBox>
            <Box sx={{ flex: 1, flexDirection: 'column', maxWidth: 1200 }}>
                <Box sx={{ mt: 4, mb: 4, '& span': { display: 'block' } }}>
                    <ArticleStatsBreadcrumbs article={article} />
                </Box>
                <Box id="article-stats-title" sx={{ mb: 2 }}>
                    <Typography variant="h5">{article.title}</Typography>
                </Box>
                <ArticleStatsBar
                    article={{ ...article, views: statusBarArticleViews }}
                />
                <AdminList
                    name="users"
                    showHeader={false}
                    items={articleStatRowData}
                    loading={loading}
                    columns={columnDefs}
                    showAdd={false}
                />
            </Box>
        </ContainerBox>
    );
};

export default ArticleStatsPage;
